// Libraries
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData, useParams, generatePath } from "react-router-dom";

// Resources
import { pathname } from "#src/config/pathname";

// Components
import { BreadcrumbV2 } from "#src/commonV2/Breadcrumb";
import LoadingState from "#src/common/system/LoadingState";
import NoResultsState from "#src/common/states/NoResultsState";
import { ContentSection } from "#src/layouts/content";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import Enable2FA from "#src/components/workspace/security/Enable2FA";
import UseEmailFromDomain from "#src/components/workspace/security/UseEmailFromDomain";

// API-related
import workspaceServices, {
  type IWorkspaceItem,
} from "#src/services/workspace";

import { getPermissionFromWorkspace, isAllowedTo } from "#src/permission";
import { PermissionAction } from "#src/permission/actions";

type LoaderType = IWorkspaceItem | null;

/** @todo: get data from loader instead of useEffect fetching. */
const WorkspaceSecurityPage = () => {
  const { t } = useTranslation("workspace", { keyPrefix: "security" });
  const { t: sidebarT } = useTranslation("layout", {
    keyPrefix: "sidebar.v2",
  });
  const { workspaceId } = useParams<"workspaceId">();

  const workspaceDetails = useLoaderData() as LoaderType;

  if (workspaceDetails === null) {
    throw Error("404");
  }

  const allowedToUpdate = isAllowedTo(
    PermissionAction.WORKSPACE_SETTINGS_UPDATE,
    getPermissionFromWorkspace(workspaceDetails)
  );

  // --------------- DATA ---------------

  const [isLoading, setLoading] = useState<boolean>(false);
  const [currentWorkspace, setCurrentWorkspace] = useState<IWorkspaceItem | null>(null);

  // TODO: be aware that this will affect workspace selector
  const fetchWorkspace = useCallback(
    (workspaceId: string) => {
      setLoading(true);
      workspaceServices
        .retrieve_workspace(workspaceId)
        .then((response) => {
          setCurrentWorkspace(response);
          setLoading(false);
        })
        .catch((error) => {
          apiErrorHandler(error, {
            toastMessage: t("notification.loadWorkspace.fail"),
          });
        });
      },
    [t]
  );

  useEffect(() => {
    if (workspaceId) {
      fetchWorkspace(workspaceId);
    }
  }, [workspaceId, fetchWorkspace]);

  // --------------- RENDER ---------------

  return <div className="flex flex-col pt-5 pb-6 gap-5 overflow-x-hidden overflow-y-auto h-full">
    {/* 1st section (Header) */}
      <div className="flex flex-col gap-5">
        {/* Header (Breadcrumb + title) */}
        <div className="px-8 flex flex-col gap-4">
          <BreadcrumbV2
            levels={[
              {
                name: sidebarT("workspaceSettings.label"),
                path: generatePath(pathname.WORKSPACE_DETAILS, {
                  workspaceId,
                }),
              },
            ]}
            currentPage={{
              name: sidebarT("workspaceSettings.security"),
            }}
          />
          <span className="text-display-xs font-semibold text-gray-v2-900">
            {t("title")}
          </span>
        </div>
      </div>
    {/* 1st section (Header) end */}
    {
      workspaceId ? (
        currentWorkspace ? (
          <ContentSection className="px-0">
            <div className="grid grid-cols-3 gap-8 px-8">
              <div className="col-span-2 flex flex-col gap-8">
                <Enable2FA
                  workspaceId={workspaceId}
                  allowedToUpdate={allowedToUpdate}
                  currentWorkspace={currentWorkspace}
                  setCurrentWorkspace={setCurrentWorkspace}
                />
                <UseEmailFromDomain
                  workspaceId={workspaceId}
                  allowedToUpdate={allowedToUpdate}
                  currentWorkspace={currentWorkspace}
                  setCurrentWorkspace={setCurrentWorkspace}
                />
              </div>
            </div>
          </ContentSection>
        ) : isLoading ? (
          <LoadingState />
        ) : (
          <NoResultsState />
        )
      ) : null
    }
  </div>
};

export default WorkspaceSecurityPage;
